import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useABTest from '../../hooks/useABTest';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';
import { isPreviewExperimentOverrideHost } from '../../utils/experimentOverrideHosts';
import './EducationDesignExperiment.css';

export const EDUCATION_DESIGN_VARIANTS = ['control', 'control_room'] as const;
export type EducationDesignVariant = typeof EDUCATION_DESIGN_VARIANTS[number];

const EDUCATION_DESIGN_SESSION_ASSIGNMENT_KEY = 'education-control-room-design-2026-08-assignment';

type EducationDesignExperimentProps = {
  children: React.ReactNode;
};

export const normalizeEducationDesignVariant = (
  value: unknown,
): EducationDesignVariant | null => (
  EDUCATION_DESIGN_VARIANTS.includes(value as EducationDesignVariant)
    ? value as EducationDesignVariant
    : null
);

const readSessionAssignment = (): EducationDesignVariant | null => {
  try {
    return normalizeEducationDesignVariant(
      window.sessionStorage.getItem(EDUCATION_DESIGN_SESSION_ASSIGNMENT_KEY),
    );
  } catch {
    return null;
  }
};

const writeSessionAssignment = (variant: EducationDesignVariant): void => {
  try {
    window.sessionStorage.setItem(EDUCATION_DESIGN_SESSION_ASSIGNMENT_KEY, variant);
  } catch {
    // Storage can be unavailable in privacy-restricted browsing contexts.
  }
};

/**
 * One shared presentation experiment for public Education destinations.
 * Child pages keep their content, data, actions, and route contracts; only
 * the surrounding visual treatment changes.
 */
export default function EducationDesignExperiment({
  children,
}: EducationDesignExperimentProps): React.ReactElement {
  const location = useLocation();
  const experiment = ACTIVE_EXPERIMENTS.EDUCATION_CONTROL_ROOM_DESIGN_2026_08;
  const featureFlagKey = experiment.featureFlag || 'education-control-room-design-2026-08';
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const previewOverride = isPreviewExperimentOverrideHost()
    ? normalizeEducationDesignVariant(searchParams.get('educationDesign'))
    : null;
  const sessionAssignment = useMemo(
    () => (previewOverride ? null : readSessionAssignment()),
    [previewOverride],
  );
  const forcedAssignment = previewOverride || sessionAssignment;

  const trackingData = useMemo(() => ({
    page: location.pathname,
    surface: 'education_destination',
    education_path: location.pathname,
    validVariants: EDUCATION_DESIGN_VARIANTS,
    skipFeatureFlagEvaluation: Boolean(forcedAssignment),
    forcedVariant: forcedAssignment || undefined,
    forcedExposureValue: sessionAssignment || undefined,
    trackForcedExposure: Boolean(sessionAssignment),
    exposure_source: previewOverride
      ? 'education_design_preview_override'
      : sessionAssignment
        ? 'education_design_session_assignment'
        : undefined,
  }), [forcedAssignment, location.pathname, previewOverride, sessionAssignment]);

  const {
    variant,
    isLoading,
    isExperimentActive,
  } = useABTest(
    experiment.name,
    featureFlagKey,
    'control',
    trackingData,
  ) as {
    variant: string | boolean | null;
    isLoading: boolean;
    isExperimentActive: boolean;
  };

  const assignedVariant = normalizeEducationDesignVariant(variant);
  const hasConfirmedActiveAssignment = Boolean(
    !isLoading && isExperimentActive && assignedVariant,
  );
  const resolvedVariant = previewOverride
    || (hasConfirmedActiveAssignment ? assignedVariant : null)
    || 'control';
  const isControlRoomDesign = resolvedVariant === 'control_room';

  useEffect(() => {
    if (!previewOverride && hasConfirmedActiveAssignment && assignedVariant) {
      writeSessionAssignment(assignedVariant);
    }
  }, [assignedVariant, hasConfirmedActiveAssignment, previewOverride]);

  return (
    <div
      className={[
        'education-design-experiment',
        isControlRoomDesign ? 'education-design-experiment--control-room' : '',
      ].filter(Boolean).join(' ')}
      data-testid="education-design-experiment"
      data-education-design-variant={resolvedVariant}
      data-experiment-active={String(hasConfirmedActiveAssignment && !previewOverride)}
      data-experiment-name={experiment.name}
    >
      {children}
    </div>
  );
}
